import { createColumnHelper } from "@tanstack/react-table";
import { EmptyValue } from "@/components/ui/record-table/empty-value";
import type { RecordGridFeatures } from "@/components/ui/record-table/grid/record-grid-features";
import type { RecordColumnDef } from "@/components/ui/record-table/column-types";
import { HoverPrefetchLink } from "@/components/ui/hover-prefetch-link";
import { listingHref } from "@shared/routing/listing-href";
import { cn } from "@/lib/utils";
import { flexibleWidth } from "@/components/ui/record-table/grid/grid-widths";
import { formatDimension, formatDimensionsCompact } from "@/lib/specs/dimensions";
import { formatSpecValue } from "@/lib/specs/labels";
import {
  MAIN_PRODUCT_TYPE_SLUGS,
  isProductType,
  type FieldDefinition,
} from "@/lib/generated/product-types";
import { getFieldsForType } from "@/lib/generated/product-type-fields";
import type { BrowseListing } from "@/components/listing/browse/marketplace-listing";
import { minimumOrderLabel } from "@/components/features/listings/product-card-display";
import { quoteHrefFor } from "@/components/listing/browse/card-grid";
import {
  ProductTypeCell,
  ProductImageCell,
  LocationCell,
  PriceCell,
  AvailabilityCell,
} from "./column-cells";
import { formatConditionLabel } from "@shared/products/product-display";

const columnHelper = createColumnHelper<RecordGridFeatures, BrowseListing>();

export type { FilterType } from "@/components/ui/record-table/column-types";
import type {
  FilterType,
  ColumnMeta as BaseColumnMeta,
} from "@/components/ui/record-table/column-types";

export interface ColumnMeta extends BaseColumnMeta {
  isSpec?: boolean;
  hiddenByDefault?: boolean;
  productTypes?: string[];
}


const slugOf = (value: unknown): string =>
  typeof value === "string" ? value : "";

const numberOrNull = (value: unknown): number | null =>
  typeof value === "number" ? value : null;

const stringOrUndefined = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;



type ProductDimensions = NonNullable<BrowseListing["dimensions"]>;

const DIMENSION_KEYS = [
  "length",
  "width",
  "height",
  "capacity",
  "unit",
  "capacityUnit",
] as const satisfies readonly (keyof ProductDimensions)[];

function isDimensionKey(key: string): key is keyof ProductDimensions {
  return DIMENSION_KEYS.some((dimensionKey) => dimensionKey === key);
}

function getSpecValue(product: BrowseListing, key: string): unknown {
  if (isDimensionKey(key)) {
    return product.dimensions ? (product.dimensions[key] ?? null) : null;
  }
  if (!product.specs) return null;
  return product.specs[key] ?? null;
}

const roundSize = (value: number | undefined) =>
  value === undefined ? undefined : Math.round(value * 10) / 10;

function sizeLabel(dimensions: BrowseListing["dimensions"]): string | null {
  if (!dimensions) return null;
  const rounded = {
    ...dimensions,
    length: roundSize(dimensions.length),
    width: roundSize(dimensions.width),
    height: roundSize(dimensions.height),
  };
  const label = formatDimension(rounded, { style: "compact" });
  const physicalOnly =
    rounded.capacity === undefined && rounded.length !== undefined;
  return label && physicalOnly && rounded.unit
    ? `${label} ${rounded.unit}`
    : label;
}

const BASE_COLUMNS: RecordColumnDef<BrowseListing, unknown>[] = [
  columnHelper.accessor((row): unknown => row.productTypeSlug, {
    id: "productType",
    header: "Type",
    size: 90,
    enableSorting: true,
    meta: { filterType: "none" } satisfies ColumnMeta,
    cell: ({ getValue }) => <ProductTypeCell slug={slugOf(getValue())} />,
  }),

  columnHelper.accessor((row): unknown => row.primaryImageUrl, {
    id: "image",
    header: "",
    size: 56,
    enableSorting: false,
    meta: { filterType: "none" } satisfies ColumnMeta,
    cell: ({ row }) => <ProductImageCell product={row.original} />,
  }),

  columnHelper.accessor(
    (row): unknown => sizeLabel(row.dimensions),
    {
      id: "dimensions",
      header: "Size",
      size: 112,
      enableSorting: false,
      meta: { filterType: "none" } satisfies ColumnMeta,
      cell: ({ getValue }) => {
        const value = stringOrUndefined(getValue());
        if (!value) return <EmptyValue />;
        return (
          <span className="whitespace-nowrap text-muted-foreground tabular-nums">
            {value}
          </span>
        );
      },
    },
  ),

  columnHelper.accessor(
    (row): unknown => {
      const city = row.cityName;
      const state = row.stateAbbr;
      if (city && state) return `${city}, ${state}`;
      return city || state || null;
    },
    {
      id: "location",
      header: "Location",
      size: 130,
      enableSorting: true,
      meta: { filterType: "none" } satisfies ColumnMeta,
      cell: ({ row }) => (
        <LocationCell
          city={row.original.cityName}
          state={row.original.stateAbbr}
        />
      ),
    },
  ),

  columnHelper.accessor((row): unknown => row.buyerUnitPriceCents, {
    id: "unitPrice",
    header: "Price",
    size: 112,
    enableSorting: true,
    meta: { filterType: "range", align: "right" } satisfies ColumnMeta,
    cell: ({ getValue, row }) => (
      <PriceCell
        cents={numberOrNull(getValue())}
        minimumOrder={minimumOrderLabel(row.original)}
      />
    ),
  }),
];

const GRADE_COLUMN = columnHelper.accessor(
  (row): unknown => formatConditionLabel(row.condition, row.productTypeSlug),
  {
    id: "grade",
    header: "Grade",
    size: 116,
    enableSorting: true,
    meta: { filterType: "none" } satisfies ColumnMeta,
    cell: ({ getValue }) => {
      const value = stringOrUndefined(getValue());
      if (!value) return <EmptyValue />;
      return <span className="whitespace-nowrap text-foreground">{value}</span>;
    },
  },
);

function availabilityColumn(
  requestedQuantity?: number,
  onSelect?: (product: BrowseListing) => void,
) {
  return columnHelper.accessor(
    (row): unknown => row.availableToSell ?? null,
    {
      id: "availability",
      header: "Availability",
      size: 136,
      enableSorting: false,
      meta: { filterType: "none" } satisfies ColumnMeta,
      cell: ({ row }) => (
        <AvailabilityCell
          product={row.original}
          quoteHref={quoteHrefFor(row.original, requestedQuantity)}
          onSelect={onSelect}
        />
      ),
    },
  );
}

export function getBaseColumns(
  requestedQuantity?: number,
  onSelect?: (product: BrowseListing) => void,
): RecordColumnDef<BrowseListing, unknown>[] {
  return [
    ...BASE_COLUMNS.slice(0, 2),
    columnHelper.accessor((row): unknown => row.title, {
      id: "title",
      header: "Product",
      enableHiding: false,
      size: 180,
      enableSorting: true,
      meta: flexibleWidth({ filterType: "text" } satisfies ColumnMeta, 420),
      cell: ({ row }) => (
        <HoverPrefetchLink
          href={listingHref(row.original, requestedQuantity)}
          onClick={() => onSelect?.(row.original)}
          className="line-clamp-2 whitespace-normal text-sm font-semibold leading-snug text-foreground hover:text-action cursor-pointer transition-colors"
        >
          {row.original.title}
        </HoverPrefetchLink>
      ),
    }),
    BASE_COLUMNS[2],
    GRADE_COLUMN,
    ...BASE_COLUMNS.slice(3),
    availabilityColumn(requestedQuantity, onSelect),
  ];
}

function sentenceCaseHeader(label: string): string {
  return label
    .split(" ")
    .map((word, index) => {
      if (index === 0) return word;
      const isAcronym = word.length > 1 && word === word.toUpperCase();
      return isAcronym || /\d/.test(word) ? word : word.toLowerCase();
    })
    .join(" ");
}

function fieldToColumn(
  field: FieldDefinition,
  hiddenByDefault: boolean,
): RecordColumnDef<BrowseListing, unknown> {
  const { name, type, label, options } = field;

  const filterType: FilterType =
    type === "select"
      ? "select"
      : type === "boolean"
        ? "boolean"
        : type === "number"
          ? "range"
          : "text";

  const filterOptions = options?.map((opt) => ({
    label: opt.label,
    value: opt.value,
  }));

  return columnHelper.accessor(
    (row): unknown => {
      if (type === "dimensions") {
        return formatDimensionsCompact(row.dimensions);
      }
      return getSpecValue(row, name);
    },
    {
      id: `spec_${name}`,
      header: sentenceCaseHeader(label),
      size: type === "boolean" ? 50 : 90,
      enableSorting: type === "select" || type === "boolean",
      meta: { filterType, filterOptions, isSpec: true, hiddenByDefault } satisfies ColumnMeta,
      cell: ({ getValue }) => (
        <SpecCell value={getValue()} type={type} options={options} />
      ),
    },
  );
}

function SpecCell({
  value,
  type,
  options,
}: {
  value: unknown;
  type: string;
  options?: FieldDefinition["options"];
}) {
  if (value === null || value === undefined) {
    return <EmptyValue />;
  }

  if (type === "boolean") {
    return (
      <span
        className={cn(
          "text-xs font-medium",
          value ? "text-success-strong" : "text-muted-foreground",
        )}
      >
        {value ? "Yes" : "No"}
      </span>
    );
  }

  const formatted =
    options?.find((option) => option.value === value)?.label ?? formatSpecValue(value);
  return (
    <span className="text-xs text-muted-foreground truncate block max-w-[90px]">
      {formatted}
    </span>
  );
}

export function getColumnsForProductType(
  productTypeSlug: string | null,
  requestedQuantity?: number,
  onSelect?: (product: BrowseListing) => void,
): RecordColumnDef<BrowseListing, unknown>[] {
  const baseColumns = getBaseColumns(requestedQuantity, onSelect);

  if (!productTypeSlug) {
    return baseColumns;
  }

  if (!isProductType(productTypeSlug)) {
    return baseColumns;
  }

  const fields = getFieldsForType(productTypeSlug);
  if (fields.length === 0) {
    return baseColumns;
  }

  const specColumns = fields
    .filter(
      (field) =>
        field.name !== "condition" &&
        field.type !== "dimensions" &&
        !isDimensionKey(field.name),
    )
    .map((field, index) => fieldToColumn(field, index > 0));
  const afterGrade = baseColumns.findIndex((column) => column.id === "grade") + 1;
  return [...baseColumns.slice(0, afterGrade), ...specColumns, ...baseColumns.slice(afterGrade)];
}

export function getAllProductTypes(): string[] {
  return [...MAIN_PRODUCT_TYPE_SLUGS];
}
